import API from "../../helpers/network/http";

import {Reel} from "../../utils/types";
import {
    Challenge,
    ChallengeEntry,
    ChallengeFundingInit,
    ChallengeInterestSummary,
    ChallengeLeaderboardEntry,
    ChallengeEntryReview,
    ChallengeModerationLog,
    ChallengeModerator,
    CommunityLookupUser,
    DisqualifiedChallengeEntry
} from "./types";

export default class CommunityService {
    private static isUuidLikeReelId(id: string) {
        return /^[A-Za-z0-9_-]{8,}$/.test(id) && !/^\d+$/.test(id);
    }
    // Create a method to create a song request
    static async createReel(payload: Partial<Reel>) {
        // Call the register endpoint from the API
        const {data} = await API.post("/reels", payload);

        // Return the response
        return data.data;
    }

    // Method to get reels
    static async getReels(query: any): Promise<Reel[]> {
        console.log("Fetching reels with query: ", query);
        // Call the login endpoint from the API
        const {data} = await API.get("/reels", {params: query});

        // Return the response
        return data.data;
    }

    // Method to get reels by cursor
    static async getReelsByCursor(query: any): Promise<any> {
        const { data } = await API.get("/reels/cursor", { params: query });
        return data.data;
    }


    // Method to get a reel
    static async getReel(id: any): Promise<Reel> {
        const reelId = String(id || '').trim();
        if (!CommunityService.isUuidLikeReelId(reelId)) {
            throw new Error('Invalid reel id. Use a UUID-style identifier.');
        }

        // Call the login endpoint from the API
        const {data} = await API.get(`/reels/${reelId}`);

        // Return the response
        return data.data;
    }

    // Method to like a reel
    static async likeReel(id: any): Promise<any> {
        // Call the login endpoint from the API
        const {data} = await API.post(`/reels-like`, {
            reel_id: id
        });

        // Return the response
        return data.data;
    }

    // Method to unlike a reel
    static async unlikeReel(id: any): Promise<any> {
        // Call the login endpoint from the API
        const {data} = await API.delete(`/reels-like`, {
            data: {
                reel_id: id
            }
        });

        // Return the response
        return data.data;
    }

    static async getChallenges(query: Record<string, any> = {}): Promise<{challenges: Challenge[]; total: number; page: number; limit: number}> {
        const {data} = await API.get("/challenge/explore", {params: query});
        return data.data;
    }

    static async getUserChallenges(query: Record<string, any> = {}): Promise<{challenges: Challenge[]; total: number; page: number; limit: number}> {
        const {data} = await API.get("/challenge/user", {params: query});
        return data.data;
    }

    static async getChallengeByUuid(challengeUuid: string): Promise<Challenge> {
        const {data} = await API.get(`/challenge/${challengeUuid}`);
        return data.data;
    }

    static async getChallengeEntries(challengeId: number, query: Record<string, any> = {}): Promise<{entries: ChallengeEntry[]; total: number; page: number; limit: number}> {
        const {data} = await API.get("/challenge-entry", {
            params: {
                challenge_id: challengeId,
                ...query,
            },
        });
        return data.data;
    }

    static async startChallengeFunding(challengeId: number, provider?: string): Promise<ChallengeFundingInit> {
        const {data} = await API.post(`/challenge/${challengeId}/fund`, provider ? {provider} : {});
        return data.data;
    }

    static async reconcileChallengeFunding(challengeId: number): Promise<any> {
        const {data} = await API.post(`/challenge/${challengeId}/reconcile-funding`);
        return data.data;
    }

    static async createChallenge(payload: Record<string, any>): Promise<{challenge_id: number[]}> {
        const {data} = await API.post('/challenge', payload);
        return data.data;
    }

    static async updateChallenge(challengeId: number, payload: Record<string, any>): Promise<any> {
        const {data} = await API.put(`/challenge/${challengeId}`, payload);
        return data.data;
    }

    static async createChallengeEntry(payload: Record<string, any>): Promise<any> {
        const {data} = await API.post('/challenge-entry', payload);
        return data.data;
    }

    static async createChallengeInvite(payload: {challenge_id: number; user_email?: string; choir_id?: number}): Promise<any> {
        const {data} = await API.post('/challenge-invite', payload);
        return data.data;
    }

    static async getChallengeLeaderboard(challengeId: number): Promise<ChallengeLeaderboardEntry[]> {
        const {data} = await API.get('/challenge-leaderboard', {
            params: {
                challenge_id: challengeId,
            },
        });
        return data.data || [];
    }

    static async getChallengeInterests(challengeId: number): Promise<ChallengeInterestSummary> {
        const {data} = await API.get(`/challenge-interest/${challengeId}`);
        return data.data;
    }

    static async getUserChallengeInterest(challengeId: number): Promise<{expressed: boolean}> {
        const {data} = await API.get(`/challenge-interest/${challengeId}/user`);
        return data.data;
    }

    static async expressChallengeInterest(challengeId: number): Promise<any> {
        const {data} = await API.post('/challenge-interest', {
            challenge_id: challengeId,
        });
        return data.data;
    }

    static async getChallengeModerators(challengeId: number): Promise<ChallengeModerator[]> {
        const {data} = await API.get('/challenge-moderator', {
            params: {
                challenge_id: challengeId,
            },
        });
        return data.data || [];
    }

    static async assignChallengeModerator(payload: {challenge_id: number; user_email: string; weight: number}): Promise<any> {
        const {data} = await API.post('/challenge-moderator', payload);
        return data.data;
    }

    static async removeChallengeModerator(challengeId: number, userEmail: string): Promise<any> {
        const {data} = await API.delete(`/challenge-moderator/${challengeId}/${encodeURIComponent(userEmail)}`);
        return data.data;
    }

    static async voteChallengeEntry(payload: {entry_id: number; score: number; comment?: string}): Promise<any> {
        const {data} = await API.post('/challenge-vote', payload);
        return data.data;
    }

    static async likeChallengeEntry(entryId: number): Promise<any> {
        const {data} = await API.post('/challenge-like', {
            entry_id: entryId,
        });
        return data.data;
    }

    static async unlikeChallengeEntry(entryId: number): Promise<any> {
        const {data} = await API.post('/challenge-unlike', {
            entry_id: entryId,
        });
        return data.data;
    }

    static async disqualifyChallengeEntry(payload: {entry_id: number; reason: string}): Promise<any> {
        const {data} = await API.post('/challenge-disqualified-entry', payload);
        return data.data;
    }

    static async getDisqualifiedChallengeEntries(challengeId: number): Promise<DisqualifiedChallengeEntry[]> {
        const {data} = await API.get('/challenge-disqualified-entry', {
            params: {
                challenge_id: challengeId,
            },
        });
        return data.data || [];
    }

    static async getUserByUsername(username: string): Promise<CommunityLookupUser> {
        const {data} = await API.get(`/users/username/${encodeURIComponent(username)}/check`);
        return data.data;
    }

    static async reviewChallengeEntry(payload: {entry_id: number; comment: string; is_public?: boolean}): Promise<any> {
        const {data} = await API.post('/challenge-entry-review', payload);
        return data.data;
    }

    static async getChallengeEntryReviews(entryId: number): Promise<ChallengeEntryReview[]> {
        const {data} = await API.get('/challenge-entry-review', {
            params: {
                entry_id: entryId,
            },
        });
        return data.data || [];
    }

    static async getChallengeModerationLogs(challengeId: number): Promise<ChallengeModerationLog[]> {
        const {data} = await API.get('/challenge-moderation-log', {
            params: {
                challenge_id: challengeId,
            },
        });
        return data.data || [];
    }
}
